import {
  AT45DB_FLASH_BYTES,
  installAppIntoFlash,
  isAuthenticFlashImage,
  isCybikoAppArchive,
  listFlashSlots,
  normalizeByorAppFilename,
  type FlashSlotSummary,
} from './byor-flash';
import {
  BYOR_APP_ID,
  BYOR_DB_NAME,
  BYOR_FLASH_ID,
  BYOR_ROM_ID,
  clearByorFiles,
  getByorApp,
  getByorFlash,
  listByorManifests,
  putByorFile,
  type ByorManifestEntry,
  type ByorZipEntryManifest,
} from './byor-store';
import {
  extractZipEntry,
  isZipArchive,
  listZipEntries,
  type ZipEntryInfo,
} from './byor-zip';

export interface ByorControlsOptions {
  readonly exportRunningFlash?: () => Promise<ArrayBuffer>;
  readonly reloadPage?: () => void;
}

declare global {
  interface Window {
    __cybikoByorDebug?: {
      readonly list: () => Promise<ByorManifestEntry[]>;
      readonly clear: () => Promise<void>;
    };
  }
}

const REQUIRED_MAME_ROM_ENTRIES = [
  { name: 'cyrom117.bin', size: 32_768, crc32: '268da7bf' },
  { name: 'cyos_v1358.bin', size: 262_144, crc32: '05ca4ece' },
  { name: 'flash_v1358.bin', size: AT45DB_FLASH_BYTES, crc32: 'e485880f' },
] as const;

let statusEl: HTMLElement | undefined;
let replaceSelect: HTMLSelectElement | undefined;
let installButton: HTMLButtonElement | undefined;
let exportFlashButton: HTMLButtonElement | undefined;

export function initByorControls(options: ByorControlsOptions): void {
  const panel = document.getElementById('byor-panel');
  if (!panel) return;

  const fileInput = document.createElement('input');
  fileInput.className = 'byor-file';
  fileInput.type = 'file';
  fileInput.multiple = true;
  fileInput.accept = '.zip,.bin,.app,application/zip,application/octet-stream';

  replaceSelect = document.createElement('select');
  replaceSelect.className = 'byor-select';
  replaceSelect.setAttribute('aria-label', 'Replacement flash slot');

  installButton = button('Install app into local flash');
  installButton.disabled = true;
  installButton.addEventListener('click', () => {
    void installImportedApp();
  });

  exportFlashButton = button('Export flash state');
  exportFlashButton.addEventListener('click', () => {
    void exportFlashState(options.exportRunningFlash);
  });

  const exportManifestButton = button('Export manifest');
  exportManifestButton.addEventListener('click', () => {
    void exportByorManifest();
  });

  const resetEmulatorButton = button('Reset emulator storage');
  resetEmulatorButton.addEventListener('click', () => {
    void resetEmulatorStorage(options.reloadPage);
  });

  const clearButton = button('Clear local BYOR files');
  clearButton.addEventListener('click', () => {
    void clearByorFilesAndRefresh(options.reloadPage);
  });

  statusEl = document.createElement('div');
  statusEl.className = 'byor-status';
  statusEl.setAttribute('role', 'status');

  const heading = document.createElement('h3');
  heading.textContent = 'Local ROMs';

  const summary = document.createElement('div');
  summary.className = 'byor-summary';

  const importLabel = document.createElement('label');
  importLabel.className = 'byor-import-label';
  importLabel.textContent = 'Import local files';
  importLabel.append(fileInput);

  const installerRow = document.createElement('div');
  installerRow.className = 'byor-row';
  installerRow.append(replaceSelect, installButton);

  const actionRow = document.createElement('div');
  actionRow.className = 'byor-row byor-actions';
  actionRow.append(exportFlashButton, exportManifestButton, resetEmulatorButton, clearButton);

  panel.replaceChildren(heading, importLabel, installerRow, actionRow, summary, statusEl);

  fileInput.addEventListener('change', () => {
    void importSelectedFiles(fileInput.files);
  });

  window.__cybikoByorDebug = {
    list: listByorManifests,
    clear: clearByorFiles,
  };

  void refreshByorControls('BYOR files stay in this browser. Nothing is uploaded.');
}

async function importSelectedFiles(files: FileList | null): Promise<void> {
  if (!files || files.length === 0) return;
  const messages: string[] = [];
  for (const file of Array.from(files)) {
    try {
      messages.push(await importOneFile(file));
    } catch (err) {
      messages.push(`${file.name}: ${err instanceof Error ? err.message : String(err)}`);
    }
  }
  await refreshByorControls(messages.join(' '));
}

async function importOneFile(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  if (isZipArchive(bytes)) {
    return importZipFile(file.name, bytes);
  }
  if (isAuthenticFlashImage(bytes)) {
    const stored = await putByorFile({
      id: BYOR_FLASH_ID,
      kind: 'flash',
      name: file.name,
      bytes,
      notes: ['Authentic 540,672-byte AT45DB041 flash image imported locally.'],
    });
    return `${stored.name}: local flash imported (${shortHash(stored.sha256)}).`;
  }
  if (isCybikoAppArchive(bytes)) {
    const filename = normalizeByorAppFilename(file.name);
    if (!filename) throw new Error('could not derive a CyOS-safe app filename');
    const stored = await putByorFile({
      id: BYOR_APP_ID,
      kind: 'app',
      name: file.name,
      cyosFilename: filename,
      bytes,
      notes: [`Raw Cybiko .app imported locally as ${filename}.`],
    });
    return `${stored.name}: app imported as ${filename} (${shortHash(stored.sha256)}).`;
  }
  throw new Error('unsupported file; expected cybikov2.zip, flash .bin, raw .app, or ZIP containing .app');
}

async function importZipFile(name: string, bytes: Uint8Array): Promise<string> {
  const entries = listZipEntries(bytes);
  const manifestEntries = entries.map(toManifestEntry);
  const mameValidation = validateMameRomEntries(entries);
  if (mameValidation.isComplete) {
    const rom = await putByorFile({
      id: BYOR_ROM_ID,
      kind: 'rom-zip',
      name,
      bytes,
      entries: manifestEntries,
      notes: [
        'MAME cybikov2 ROM set imported locally.',
        'Required entry names, sizes, and CRC32 values matched docs/ROMS.md.',
      ],
    });
    const flashEntry = findEntryByBaseName(entries, 'flash_v1358.bin');
    if (flashEntry) {
      const flash = await extractZipEntry(bytes, flashEntry);
      await putByorFile({
        id: BYOR_FLASH_ID,
        kind: 'flash',
        name: 'flash_v1358.bin',
        bytes: flash,
        notes: [`Extracted from local ${name}; used for app installation and quickload.`],
      });
    }
    return `${rom.name}: local ROM set imported and verified (${shortHash(rom.sha256)}).`;
  }

  const appEntry = entries.find((entry) => !entry.isDirectory && entry.name.toLowerCase().endsWith('.app'));
  if (appEntry) {
    const appBytes = await extractZipEntry(bytes, appEntry);
    if (!isCybikoAppArchive(appBytes)) {
      throw new Error(`${appEntry.name} does not look like a Cybiko .app archive`);
    }
    const filename = normalizeByorAppFilename(baseName(appEntry.name));
    if (!filename) throw new Error(`could not derive a CyOS-safe filename from ${appEntry.name}`);
    const stored = await putByorFile({
      id: BYOR_APP_ID,
      kind: 'app',
      name: appEntry.name,
      cyosFilename: filename,
      bytes: appBytes,
      entries: manifestEntries,
      notes: [`Extracted from local ZIP ${name}; install requires an imported flash image.`],
    });
    return `${stored.name}: app extracted as ${filename} (${shortHash(stored.sha256)}).`;
  }

  throw new Error(`ZIP did not match cybikov2 ROM entries: ${mameValidation.notes.join('; ')}`);
}

async function installImportedApp(): Promise<void> {
  if (!replaceSelect) return;
  const app = await getByorApp();
  const flash = await getByorFlash();
  if (!app) {
    await refreshByorControls('Import a local .app before installing.');
    return;
  }
  if (!flash) {
    await refreshByorControls('Import a cybikov2 ROM set or authentic flash image before installing apps.');
    return;
  }
  const filename = app.cyosFilename;
  if (!filename) {
    await refreshByorControls('Imported app is missing a CyOS-safe filename.');
    return;
  }
  try {
    const result = installAppIntoFlash(
      new Uint8Array(flash.bytes),
      new Uint8Array(app.bytes),
      { filename, replace: replaceSelect.value },
    );
    const stored = await putByorFile({
      id: BYOR_FLASH_ID,
      kind: 'flash',
      name: 'byor-flash.bin',
      bytes: result.flash,
      notes: [
        `${result.filename} installed over ${result.replacedFilename}.`,
        `Used ${result.blockCount} flash blocks; app ${result.appBytes} bytes; slot capacity ${result.capacityBytes} bytes.`,
      ],
    });
    await refreshByorControls(
      `${result.filename} installed locally over ${result.replacedFilename}. Reset emulator storage before booting this flash (${shortHash(stored.sha256)}).`,
    );
  } catch (err) {
    await refreshByorControls(err instanceof Error ? err.message : String(err));
  }
}

async function refreshByorControls(message?: string): Promise<void> {
  const manifests = await listByorManifests();
  const flash = await getByorFlash();
  const app = await getByorApp();
  const slots = flash ? listFlashSlots(new Uint8Array(flash.bytes)) : [];
  renderSlotSelect(slots);
  renderSummary(manifests);
  if (installButton) installButton.disabled = !app || !flash || slots.length === 0;
  if (exportFlashButton) exportFlashButton.disabled = false;
  if (statusEl) statusEl.textContent = message ?? '';
}

function renderSlotSelect(slots: readonly FlashSlotSummary[]): void {
  if (!replaceSelect) return;
  replaceSelect.replaceChildren();
  if (slots.length === 0) {
    const option = document.createElement('option');
    option.value = '';
    option.textContent = 'No local flash slots';
    replaceSelect.append(option);
    replaceSelect.disabled = true;
    return;
  }
  for (const slot of slots) {
    const option = document.createElement('option');
    option.value = slot.filename;
    option.textContent = `${slot.filename} (${slot.capacityBytes} bytes)`;
    replaceSelect.append(option);
  }
  replaceSelect.disabled = false;
}

function renderSummary(manifests: readonly ByorManifestEntry[]): void {
  const summary = document.querySelector<HTMLElement>('#byor-panel .byor-summary');
  if (!summary) return;
  if (manifests.length === 0) {
    summary.textContent = 'No local ROM/app files stored.';
    return;
  }
  const list = document.createElement('ul');
  for (const item of manifests) {
    const li = document.createElement('li');
    const cyos = item.cyosFilename ? ` as ${item.cyosFilename}` : '';
    li.textContent = `${item.kind}: ${item.name}${cyos}, ${item.size} bytes, sha256 ${shortHash(item.sha256)}`;
    list.append(li);
  }
  summary.replaceChildren(list);
}

async function exportFlashState(
  exportRunningFlash: (() => Promise<ArrayBuffer>) | undefined,
): Promise<void> {
  try {
    const running = exportRunningFlash ? await exportRunningFlash().catch(() => null) : null;
    if (running) {
      downloadBytes('cybiko-current-flash.bin', new Uint8Array(running), 'application/octet-stream');
      await refreshByorControls('Downloaded current emulator flash state locally.');
      return;
    }
    const flash = await getByorFlash();
    if (!flash) {
      await refreshByorControls('No local flash is available to export.');
      return;
    }
    downloadBytes('cybiko-byor-flash.bin', new Uint8Array(flash.bytes), 'application/octet-stream');
    await refreshByorControls('Downloaded stored BYOR flash locally.');
  } catch (err) {
    await refreshByorControls(err instanceof Error ? err.message : String(err));
  }
}

async function exportByorManifest(): Promise<void> {
  const manifests = await listByorManifests();
  const payload = {
    exportedAt: new Date().toISOString(),
    note: 'Metadata only. ROM/app/flash binary bytes are not included.',
    files: manifests,
  };
  const bytes = new TextEncoder().encode(JSON.stringify(payload, null, 2));
  downloadBytes('cybiko-byor-manifest.json', bytes, 'application/json');
  await refreshByorControls('Downloaded BYOR metadata manifest locally.');
}

async function resetEmulatorStorage(reloadPage: (() => void) | undefined): Promise<void> {
  const names = ['EM_FS_cybiko', 'EM_FS_/cybiko', '/cybiko', 'cybiko'];
  await Promise.all(names.map((name) => deleteIndexedDatabase(name)));
  await refreshByorControls('Emulator storage reset. Reloading will keep BYOR files but rebuild flash from the local import.');
  reloadPage?.();
}

async function clearByorFilesAndRefresh(reloadPage: (() => void) | undefined): Promise<void> {
  await clearByorFiles();
  await refreshByorControls('Local BYOR files cleared.');
  reloadPage?.();
}

function validateMameRomEntries(entries: readonly ZipEntryInfo[]): {
  readonly isComplete: boolean;
  readonly notes: readonly string[];
} {
  const notes: string[] = [];
  let complete = true;
  for (const required of REQUIRED_MAME_ROM_ENTRIES) {
    const entry = findEntryByBaseName(entries, required.name);
    if (!entry) {
      complete = false;
      notes.push(`missing ${required.name}`);
      continue;
    }
    if (entry.uncompressedSize !== required.size) {
      complete = false;
      notes.push(`${required.name} size ${entry.uncompressedSize} != ${required.size}`);
    }
    if (entry.crc32 !== required.crc32) {
      complete = false;
      notes.push(`${required.name} CRC32 ${entry.crc32} != ${required.crc32}`);
    }
  }
  if (complete) notes.push('required cybikov2 names, sizes, and CRC32 values matched');
  return { isComplete: complete, notes };
}

function findEntryByBaseName(entries: readonly ZipEntryInfo[], wanted: string): ZipEntryInfo | undefined {
  const lowered = wanted.toLowerCase();
  return entries.find((entry) => baseName(entry.name).toLowerCase() === lowered);
}

function toManifestEntry(entry: ZipEntryInfo): ByorZipEntryManifest {
  return {
    name: entry.name,
    method: entry.method,
    crc32: entry.crc32,
    compressedSize: entry.compressedSize,
    uncompressedSize: entry.uncompressedSize,
  };
}

function baseName(path: string): string {
  return path.split(/[\\/]/).pop() ?? path;
}

function button(label: string): HTMLButtonElement {
  const el = document.createElement('button');
  el.type = 'button';
  el.className = 'byor-button';
  el.textContent = label;
  return el;
}

function downloadBytes(filename: string, bytes: Uint8Array, type: string): void {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  const url = URL.createObjectURL(new Blob([copy.buffer], { type }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function deleteIndexedDatabase(name: string): Promise<void> {
  if (name === BYOR_DB_NAME) return Promise.resolve();
  return new Promise((resolve) => {
    const request = indexedDB.deleteDatabase(name);
    request.onsuccess = () => resolve();
    request.onblocked = () => resolve();
    request.onerror = () => resolve();
  });
}

function shortHash(hash: string): string {
  return hash.slice(0, 12);
}
